import { useCallback, useEffect, useState } from 'react';
import { useMediaQueries } from '../../contexts/media-query';
import { storyBlockApi } from '../../utils/apiClient';
import { formatBlogPosts } from '../../views/blogs/blogHelpers';

interface UseBlogDataResult {
  loading: boolean;
  blogPosts: any[];
  total: number;
  error: Error | null;
}

const useBlogData = (selectedType: string, page: number): UseBlogDataResult => {
  const [loading, setLoading] = useState(true);
  const [blogPosts, setBlogPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const { isLessThan1150 } = useMediaQueries();

  const fetchBlogData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await storyBlockApi({
        url: 'blogs',
        tag: selectedType,
        page,
        viewMore: !!isLessThan1150,
      });

      const total = +response.headers.total;
      const blogs = response.data?.stories || [];
      const formattedBlogs = formatBlogPosts(blogs);

      return { formattedBlogs, total };
    } catch (err) {
      console.error('Failed to load blog data:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedType, page, isLessThan1150]);

  useEffect(() => {
    let didCancel = false;

    const loadData = async () => {
      try {
        const { formattedBlogs, total } = await fetchBlogData();

        if (!didCancel) {
          setBlogPosts(formattedBlogs);
          setTotal(total);
        }
      } catch (err) {
        if (!didCancel) {
          setError(err instanceof Error ? err : new Error('Failed to load blog data'));
        }
      }
    };

    loadData();

    return () => {
      didCancel = true;
    };
  }, [fetchBlogData]);

  return { loading, blogPosts, total, error };
};

export default useBlogData;
